import React from 'react';
import { MdInfo } from 'react-icons/md';
import ReactSelect, { components, OptionProps } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import styled from 'styled-components';

import { ColorVariantEnum, FinePrintEnum, MarginSizeEnum, Text, WeightEnum } from '../../atoms/text';
import { theme } from '../../theme';
import { FoleonLoader } from '../foleon-loader';
import { Tooltip } from '../tooltip';
import { selectStyles } from './select.styles';
import { ISelect, ISelectOption } from './select.types';

const SytledLabelContainer = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
`;
const StyledDescriptionContainer = styled.div`
	margin-top: 4px;
	width: 100%;
`;

const CustomOptionContainer = styled.div<{ isSelected: boolean; isFocused: boolean; isDisabled: boolean }>`
	padding: 8px;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	border-radius: 4px;
	margin: 0 auto 12px auto;
	cursor: ${({ isDisabled }) => (isDisabled ? `not-allowed` : `pointer`)};
	background: ${({ isSelected, isFocused }) =>
		isSelected
			? theme.colors.select.option.background.selected
			: isFocused
			? theme.colors.select.option.background.hover
			: theme.colors.select.option.background.default};
	:hover {
		background: ${({ isSelected, isDisabled }) =>
			isSelected
				? theme.colors.select.option.background.selected
				: isDisabled
				? theme.colors.select.option.background.default
				: theme.colors.select.option.background.hover};
	}
`;

const WithStatusContainer = styled.div`
	display: flex;
	align-items: center;
`;

const StatusCircle = styled.div<{ status: string }>`
	width: 12px;
	height: 12px;
	border-radius: 100%;
	margin-right: 12px;
	background-color: ${({ status }) => theme.colors.select.option.templateStatus[status]};
`;

const CustomOption = (props: OptionProps<ISelectOption, true>) => {
	if (props.data.description) {
		return (
			<CustomOptionContainer
				ref={props.innerRef}
				{...props.innerProps}
				isDisabled={props.isDisabled}
				isSelected={props.isSelected}
				isFocused={props.isFocused}
			>
				<WithStatusContainer>
					{props.data.status && <StatusCircle status={props.data.status} />}
					<Text.Paragraph
						margin={MarginSizeEnum.NONE}
						colorVariant={props.isDisabled ? ColorVariantEnum.DISABLED : ColorVariantEnum.DARK}
					>
						{props.data.label}
					</Text.Paragraph>
				</WithStatusContainer>
				<Text.FinePrint
					margin={MarginSizeEnum.NONE}
					type={FinePrintEnum.CAPTION}
					colorVariant={props.isDisabled ? ColorVariantEnum.DISABLED : ColorVariantEnum.LIGHT}
				>
					{props.data.description}
				</Text.FinePrint>
			</CustomOptionContainer>
		);
	}
	return <components.Option {...props} />;
};

const CustomNoOptionsLoaderContainer = styled.div`
	padding: 16px;
`;

const CustomNoOptionsLoader = (loadingOptionsText: string) => (props: OptionProps<ISelectOption, true>) => {
	return (
		<components.NoOptionsMessage {...props} {...props.innerProps}>
			<CustomNoOptionsLoaderContainer>
				<FoleonLoader text={loadingOptionsText} />
			</CustomNoOptionsLoaderContainer>
		</components.NoOptionsMessage>
	);
};

export const Select = ({
	selected,
	options,
	onChange,
	dataTestId,
	placeholder,
	label,
	description,
	tooltipText,
	loadingOptionsText,
	bodyTarget,
	menuWidth,
	isSearchable,
	isMulti,
	isDisabled,
	isCreatable,
}: ISelect) => {
	const getSelectValue = () =>
		Array.isArray(selected)
			? selected.filter((s) => {
					if (s.__isNew__) {
						return s;
					}
					return options.find((o) => o.value === s.value);
			  })
			: options.find((option) => option.value === selected);
	return (
		<React.Fragment>
			{label && (
				<SytledLabelContainer>
					<Text.FinePrint
						type={FinePrintEnum.CAPTION}
						margin={MarginSizeEnum.NONE}
						colorVariant={ColorVariantEnum.DARK}
						weight={WeightEnum.SEMIBOLD}
					>
						{label}
					</Text.FinePrint>
					{tooltipText && (
						<Tooltip.Default text={tooltipText}>
							<MdInfo color={theme.colors.input.tooltip} size={17} />
						</Tooltip.Default>
					)}
				</SytledLabelContainer>
			)}
			{isCreatable ? (
				<CreatableSelect
					styles={{
						...selectStyles,
						menu: (provided: React.CSSProperties) => ({
							...provided,
							...selectStyles.menu,
							width: menuWidth || '100%',
						}),
					}}
					options={options}
					components={loadingOptionsText ? { NoOptionsMessage: CustomNoOptionsLoader(loadingOptionsText) } : {}}
					onChange={(value: ISelectOption | ISelectOption[]) => onChange(value)}
					value={getSelectValue()}
					menuPlacement="auto"
					placeholder={placeholder}
					isClearable={false}
					isMulti={isMulti}
					isDisabled={isDisabled}
					data-testid={dataTestId}
				/>
			) : (
				<ReactSelect
					styles={{
						...selectStyles,
						menu: (provided: React.CSSProperties) => ({
							...provided,
							...selectStyles.menu,
							width: menuWidth || '100%',
						}),
					}}
					components={{ Option: CustomOption }}
					options={options}
					onChange={onChange}
					value={getSelectValue()}
					menuPlacement="auto"
					placeholder={placeholder}
					menuPortalTarget={bodyTarget ? document.body : null}
					isClearable={false}
					isSearchable={isSearchable}
					isMulti={isMulti}
					isDisabled={isDisabled}
					data-testid={dataTestId}
				/>
			)}
			{description && (
				<StyledDescriptionContainer>
					<Text.FinePrint type={FinePrintEnum.TINY} margin={MarginSizeEnum.NONE} colorVariant={ColorVariantEnum.LIGHT}>
						{description}
					</Text.FinePrint>
				</StyledDescriptionContainer>
			)}
		</React.Fragment>
	);
};
